import { ReactNode, useEffect, useRef } from 'react'

import styled from '@emotion/styled'

interface Props {
  onClickOutside: () => void
  enable?: boolean
  children: ReactNode
}

const ZIndex = styled.div<{ value: number }>`
  position: relative;
  z-index: ${props => props.value};
`

export function DatePickerInputClickOutside({ onClickOutside, enable, children }: Props) {
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const isOutsideContainer = (target: unknown) => {
      if (!(target instanceof Element)) return true
      if (container.current?.contains(target)) return false

      // Check if the target is inside a dialog (portaled date picker)
      const dialogElement = (target as Element).closest('[role="dialog"]')
      return !dialogElement
    }

    // Prevents focus from scrolling back to first element when in a focus trap,
    // this only appears to have been an issue in Chrome.
    // Additionally the next tab will focus the next tabbable element.
    const blurInPlace = () => {
      const temp = document.createElement('button')
      temp.ariaHidden = 'true'
      temp.style.position = 'absolute'
      temp.style.opacity = '0'
      temp.tabIndex = 1000
      container.current?.append(temp)
      setTimeout(() => temp.remove())
    }

    const onClick = (event: MouseEvent) => {
      if (isOutsideContainer(event.target)) {
        blurInPlace()
        onClickOutside()
      }
    }

    const onFocusIn = (event: FocusEvent) => {
      if (isOutsideContainer(event.target)) {
        onClickOutside()
      }
    }

    if (enable) {
      // The "click" event happens on mouse up. Capture is set to run before any other event
      // listeners that may stop propogation such as a modal backdrop.
      document.addEventListener('click', onClick, { capture: true })
      // The "focusin" event can happen on mouse down which is before a "click" event,
      // this listener avoids two pickers from being up at once if going from one DatePickerInput to another DatePickerInput.
      document.addEventListener('focusin', onFocusIn, { capture: true })
    }

    return () => {
      document.removeEventListener('click', onClick, { capture: true })
      document.removeEventListener('focusin', onFocusIn, { capture: true })
    }
  }, [enable, onClickOutside])

  return (
    <ZIndex value={enable ? 1 : 0} ref={container}>
      {children}
    </ZIndex>
  )
}
